import { type PointerEvent as ReactPointerEvent, useEffect, useRef, useState } from 'react';
import OpenSeadragon from 'openseadragon';
import type { Anchor, AnchorType, CommentThread, Page, VersionDetail } from '@katsquare/types';
import {
  IconBox,
  IconChevronDown,
  IconChevronUp,
  IconDraw,
  IconFit,
  IconHand,
  IconPin,
  IconSearch,
  IconText,
  IconZoomIn,
  IconZoomOut,
} from '../../components/icons';

/** The armed tool: an annotation tool, hand (pan), or null when navigating. */
export type DrawTool = 'pin' | 'box' | 'freehand' | 'text' | 'hand';

/** Imperative zoom controls, handed to the parent so it can host them. */
export interface ZoomApi {
  in: () => void;
  out: () => void;
  fit: () => void;
}

/** Floating toolbar tools, in order. */
const TOOLBAR: { tool: DrawTool; label: string; Icon: typeof IconText }[] = [
  { tool: 'text', label: 'Select text', Icon: IconText },
  { tool: 'hand', label: 'Pan / move the page', Icon: IconHand },
  { tool: 'pin', label: 'Pin comment', Icon: IconPin },
  { tool: 'freehand', label: 'Draw', Icon: IconDraw },
  { tool: 'box', label: 'Box', Icon: IconBox },
];

interface TextWord {
  x: number;
  y: number;
  w: number;
  h: number;
  text: string;
}

interface SearchMatch {
  pageIndex: number;
  x: number;
  y: number;
  w: number;
  h: number;
}

/** An anchor placed but not yet saved. */
export interface PendingAnchor {
  anchorType: AnchorType;
  anchor: Anchor;
}

export interface ProofViewerProps {
  version: VersionDetail;
  pageIndex: number;
  onPageChange: (index: number) => void;
  threads: CommentThread[];
  tool: DrawTool | null;
  pending: PendingAnchor | null;
  onPlaceAnchor: (anchorType: AnchorType, anchor: Anchor) => void;
  selectedThreadId: string | null;
  onSelectThread: (threadId: string | null) => void;
  onReady?: (zoom: ZoomApi) => void;
  /** Switch the armed tool (from the floating toolbar). */
  onSelectTool?: (tool: DrawTool) => void;
  /** Hands the raw OpenSeadragon viewer to the parent (used to sync compare). */
  onViewer?: (viewer: OpenSeadragon.Viewer) => void;
  /** When false, hides search/thumbnails/toolbar and the custom zoom handler
   * (for the compare reveal, which drives OpenSeadragon's own nav instead). */
  interactive?: boolean;
}

const NS = 'http://www.w3.org/2000/svg';
const clamp01 = (n: number) => Math.min(1, Math.max(0, n));

/**
 * Thread id of the comment marker under a point, if any. Walks everything at
 * that spot rather than just the topmost element, so a marker still answers
 * through the transparent drawing-capture layer stacked over it.
 */
function markerAt(clientX: number, clientY: number): string | null {
  for (const el of document.elementsFromPoint(clientX, clientY)) {
    if (el instanceof HTMLElement && el.classList.contains('ksq-marker')) {
      return el.dataset.threadId ?? null;
    }
  }
  return null;
}

/** Local, in-progress drag captured in element pixel space for live preview. */
type Drag =
  | { kind: 'box'; x0: number; y0: number; x1: number; y1: number }
  | { kind: 'freehand'; px: [number, number][] };

/**
 * Deep-zoom proof viewer. Draws two overlay layers over the page tiles — print
 * guides and anchored comment annotations (pin / box / freehand) — and, when a
 * tool is armed, captures pointer input to place new annotations in normalized
 * image coordinates.
 */
export function ProofViewer(props: ProofViewerProps) {
  const { version, pageIndex, tool } = props;
  const pages = version.pages;
  const page: Page | undefined = pages[pageIndex] ?? pages[0];

  const hostRef = useRef<HTMLDivElement>(null);
  const captureRef = useRef<HTMLDivElement>(null);
  const zoomHostRef = useRef<HTMLDivElement>(null);
  const pointerDownRef = useRef<{ x: number; y: number } | null>(null);
  const viewerRef = useRef<OpenSeadragon.Viewer | null>(null);
  // Timestamp of the last wheel-driven page change, to pace scroll-to-page.
  const lastNavRef = useRef(0);
  const [drag, setDrag] = useState<Drag | null>(null);
  // While the space bar is held, all tools yield to hand-panning the page.
  const [spaceHeld, setSpaceHeld] = useState(false);
  // In-document search state.
  const [search, setSearch] = useState('');
  const [matches, setMatches] = useState<SearchMatch[]>([]);
  const [matchIdx, setMatchIdx] = useState(-1);
  const textCacheRef = useRef<Map<number, TextWord[]>>(new Map());
  const searchMatchRef = useRef<SearchMatch | null>(null);

  // Text layer (selectable words overlaid on the page for TEXT anchoring).
  const textLayerRef = useRef<HTMLDivElement>(null);
  const textGeomRef = useRef<{ rasterWidth: number; rasterHeight: number } | null>(null);
  const syncTextRef = useRef<() => void>(() => {});

  // Touch text selection. Native selection can't work on a phone (the word spans
  // have to stay pointer-transparent so the page can be panned/pinched), so touch
  // gets its own long-press-and-drag selection over the same word boxes: these
  // refs hold the rendered words in reading order and the selected index range.
  const wordsRef = useRef<TextWord[]>([]);
  const spansRef = useRef<HTMLSpanElement[]>([]);
  const touchSelRef = useRef<{ a: number; h: number } | null>(null);
  // Set while a long-press selection owns the gesture, so the pointerup that ends
  // it isn't also read as a tap (which would drop a pin or clear the selection).
  const tapGuardRef = useRef(false);
  const [touchSelActive, setTouchSelActive] = useState(false);
  const [coarse] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches,
  );

  // Latest props/page for handlers and overlay redraws bound outside React.
  const state = useRef(props);
  state.current = props;
  const pageRef = useRef(page);
  pageRef.current = page;

  useEffect(() => {
    if (!hostRef.current) return;
    const viewer = OpenSeadragon({
      element: hostRef.current,
      prefixUrl: '',
      showNavigationControl: false,
      showNavigator: false,
      visibilityRatio: 1,
      minZoomImageRatio: 0.8,
      maxZoomPixelRatio: 4,
      gestureSettingsMouse: { clickToZoom: false, scrollToZoom: state.current.interactive !== false },
      immediateRender: false,
    });
    viewerRef.current = viewer;
    state.current.onViewer?.(viewer);
    state.current.onReady?.({
      in: () => void viewer.viewport.zoomBy(1.4).applyConstraints(),
      out: () => void viewer.viewport.zoomBy(0.7).applyConstraints(),
      fit: () => void viewer.viewport.goHome(),
    });

    // Keep the text layer aligned with the image as the viewport moves/zooms.
    const syncText = () => {
      const layer = textLayerRef.current;
      const geom = textGeomRef.current;
      if (!layer || !geom || !viewer.isOpen()) return;
      const o = viewer.viewport.imageToViewerElementCoordinates(new OpenSeadragon.Point(0, 0));
      const px = viewer.viewport.imageToViewerElementCoordinates(
        new OpenSeadragon.Point(geom.rasterWidth, 0),
      );
      const scale = (px.x - o.x) / geom.rasterWidth;
      layer.style.transform = `translate(${o.x}px, ${o.y}px) scale(${scale})`;
    };
    syncTextRef.current = syncText;
    viewer.addHandler('update-viewport', syncText);
    viewer.addHandler('animation', syncText);
    viewer.addHandler('resize', syncText);
    viewer.addHandler('open', syncText);

    return () => {
      viewer.destroy();
      viewerRef.current = null;
    };
  }, []);

  // Populate / clear the selectable text layer when the text tool or page changes.
  useEffect(() => {
    const layer = textLayerRef.current;
    wordsRef.current = [];
    spansRef.current = [];
    touchSelRef.current = null;
    setTouchSelActive(false);
    if (tool !== 'text' || !page?.textUrl || !layer) {
      textGeomRef.current = null;
      if (layer) layer.replaceChildren();
      return;
    }
    let cancelled = false;
    layer.replaceChildren();
    const rw = page.rasterWidth;
    const rh = page.rasterHeight;
    // On touch screens the word spans must not take pointer events: they sit
    // above the OpenSeadragon canvas, so they would swallow every drag/pinch and
    // leave the page unpannable and unzoomable. (A tap still drops a pin, since
    // that is handled on the container.) Mouse users keep drag-to-select.
    const coarse = window.matchMedia('(pointer: coarse)').matches;
    fetch(page.textUrl, { credentials: 'include' })
      .then((r) => r.json())
      .then((data: { words: TextWord[] }) => {
        if (cancelled) return;
        textGeomRef.current = { rasterWidth: rw, rasterHeight: rh };
        layer.style.width = `${rw}px`;
        layer.style.height = `${rh}px`;
        layer.style.transformOrigin = '0 0';
        // Render spans in column-aware reading order so the browser's DOM-order
        // text selection matches what the user visually drags over. Without this,
        // multi-column pages interleave the columns and a left-column drag also
        // grabs the right column.
        const ordered = readingOrder(data.words ?? []);
        const frag = document.createDocumentFragment();
        const spans: HTMLSpanElement[] = [];
        for (const wd of ordered) {
          const s = document.createElement('span');
          s.textContent = wd.text;
          s.dataset.x = String(wd.x);
          s.dataset.y = String(wd.y);
          s.dataset.w = String(wd.w);
          s.dataset.h = String(wd.h);
          s.style.position = 'absolute';
          s.style.left = `${wd.x * rw}px`;
          s.style.top = `${wd.y * rh}px`;
          s.style.height = `${wd.h * rh}px`;
          s.style.fontSize = `${wd.h * rh}px`;
          s.style.lineHeight = '1';
          s.style.color = 'transparent';
          s.style.whiteSpace = 'pre';
          s.style.cursor = 'text';
          s.style.pointerEvents = coarse ? 'none' : 'auto';
          s.style.transformOrigin = '0 0';
          frag.appendChild(s);
          spans.push(s);
        }
        layer.appendChild(frag);
        wordsRef.current = ordered;
        spansRef.current = spans;
        // Squeeze/stretch each span to its real word width so the browser's
        // selection highlight lines up with the rasterised text instead of
        // rendering wider than it (fallback fonts have different metrics). Read
        // all widths first, then write all transforms, to avoid layout thrash.
        const widths = spans.map((s) => s.offsetWidth);
        spans.forEach((s, i) => {
          const target = Number(s.dataset.w) * rw;
          const actual = widths[i]!;
          if (actual > 0) s.style.transform = `scaleX(${target / actual})`;
        });
        syncTextRef.current();
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tool, page?.id]);

  // Wheel: a plain scroll pages through the document; ⌘/Ctrl+scroll zooms the PDF
  // toward the cursor — never the browser page. The listener lives on the
  // container in the capture phase so it works under every tool: the
  // annotation-capture layer and text spans sit above the OpenSeadragon canvas and
  // would otherwise swallow the wheel, so it only worked in text mode. Handling it
  // here (and stopping propagation) makes it behave identically for Text/Pin/Box/Draw.
  useEffect(() => {
    const el = zoomHostRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (state.current.interactive === false) return;
      const viewer = viewerRef.current;
      if (!viewer || !viewer.isOpen()) return;
      e.preventDefault(); // stop page scroll / the browser's ctrl-zoom
      e.stopPropagation(); // and stop OSD from double-zooming
      let dy = e.deltaY;
      if (e.deltaMode === 1) dy *= 16; // lines → ~px
      else if (e.deltaMode === 2) dy *= el.clientHeight; // pages → ~px

      if (e.ctrlKey || e.metaKey) {
        const rect = el.getBoundingClientRect();
        const pixel = new OpenSeadragon.Point(e.clientX - rect.left, e.clientY - rect.top);
        viewer.viewport.zoomBy(Math.exp(-dy * 0.0018), viewer.viewport.pointFromPixel(pixel));
        viewer.viewport.applyConstraints();
        return;
      }

      // Plain scroll → change page (cooldown-paced, direction from scroll sign).
      if (Math.abs(dy) < 2) return;
      const now = Date.now();
      if (now - lastNavRef.current < 260) return;
      const total = state.current.version.pages.length;
      const dir = dy > 0 ? 1 : -1;
      const next = Math.min(total - 1, Math.max(0, state.current.pageIndex + dir));
      if (next !== state.current.pageIndex) {
        lastNavRef.current = now;
        state.current.onPageChange(next);
      }
    };
    el.addEventListener('wheel', onWheel, { passive: false, capture: true });
    return () => el.removeEventListener('wheel', onWheel, { capture: true });
  }, []);

  // Touch: swipe up/down to page through the document — the gesture equivalent
  // of the wheel handler above. Only while the page is at fit-zoom (zoomed in, a
  // vertical drag means "pan"), only with a single finger (two = pinch), and only
  // under the non-drawing tools, so it can never steal a mark-up drag.
  useEffect(() => {
    const el = zoomHostRef.current;
    if (!el) return;
    let start: { x: number; y: number } | null = null;
    let fingers = 0;
    const down = (e: PointerEvent) => {
      if (e.pointerType !== 'touch') return;
      fingers++;
      start = fingers === 1 ? { x: e.clientX, y: e.clientY } : null;
    };
    const up = (e: PointerEvent) => {
      if (e.pointerType !== 'touch') return;
      const from = start;
      fingers = Math.max(0, fingers - 1);
      start = null;
      const s = state.current;
      if (!from || fingers > 0 || s.interactive === false) return;
      if (tapGuardRef.current) return; // the gesture was a text selection, not a swipe
      if (s.tool && s.tool !== 'text' && s.tool !== 'hand') return; // a drawing drag
      const viewer = viewerRef.current;
      if (!viewer || !viewer.isOpen()) return;
      if (viewer.viewport.getZoom(true) > viewer.viewport.getHomeZoom() * 1.05) return;
      const dx = e.clientX - from.x;
      const dy = e.clientY - from.y;
      if (Math.abs(dy) < 60 || Math.abs(dy) < Math.abs(dx) * 1.2) return;
      const dir = dy < 0 ? 1 : -1; // swipe up → next page
      const next = Math.min(s.version.pages.length - 1, Math.max(0, s.pageIndex + dir));
      if (next !== s.pageIndex) s.onPageChange(next);
    };
    const cancel = () => {
      fingers = Math.max(0, fingers - 1);
      start = null;
    };
    el.addEventListener('pointerdown', down);
    el.addEventListener('pointerup', up);
    el.addEventListener('pointercancel', cancel);
    return () => {
      el.removeEventListener('pointerdown', down);
      el.removeEventListener('pointerup', up);
      el.removeEventListener('pointercancel', cancel);
    };
  }, []);

  // ── Touch text selection ───────────────────────────────────────────────────
  // Paint the current touch selection onto the word spans (they're transparent
  // text over the raster, so a background is the highlight, and it follows the
  // page automatically as the layer is transformed).
  const paintTouchSelection = () => {
    const sel = touchSelRef.current;
    const lo = sel ? Math.min(sel.a, sel.h) : -1;
    const hi = sel ? Math.max(sel.a, sel.h) : -2;
    spansRef.current.forEach((s, i) => {
      s.style.background = i >= lo && i <= hi ? 'rgba(245,158,11,0.4)' : '';
    });
  };
  const clearTouchSelection = () => {
    if (!touchSelRef.current) return;
    touchSelRef.current = null;
    paintTouchSelection();
    setTouchSelActive(false);
  };
  /**
   * Index of the word at a client point, in the rendered reading order. Hit-tests
   * against the word boxes rather than the DOM (the spans are pointer-transparent
   * on touch, so elementFromPoint can't see them), falling back to the nearest
   * word within `slopPx` — dragging into a margin should still extend to the end
   * of the line rather than freeze.
   */
  const wordIndexAt = (clientX: number, clientY: number, slopPx: number): number => {
    const layer = textLayerRef.current;
    const words = wordsRef.current;
    if (!layer || words.length === 0) return -1;
    const rect = layer.getBoundingClientRect();
    if (rect.width < 1 || rect.height < 1) return -1;
    const nx = (clientX - rect.left) / rect.width;
    const ny = (clientY - rect.top) / rect.height;
    // Normalized distance is anisotropic, so weight y by the page aspect to keep
    // "nearest" meaning nearest on screen.
    const aspect = rect.height / rect.width;
    let best = -1;
    let bestD = Infinity;
    for (let i = 0; i < words.length; i++) {
      const w = words[i]!;
      const dx = nx < w.x ? w.x - nx : nx > w.x + w.w ? nx - (w.x + w.w) : 0;
      const dy = ny < w.y ? w.y - ny : ny > w.y + w.h ? ny - (w.y + w.h) : 0;
      if (dx === 0 && dy === 0) return i;
      const d = Math.hypot(dx, dy * aspect);
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    }
    return bestD * rect.width <= slopPx ? best : -1;
  };
  // Turn the selected word range into a TEXT anchor (one rect per line).
  const commitTouchSelection = () => {
    const sel = touchSelRef.current;
    const words = wordsRef.current;
    if (!sel) return;
    const chosen = words.slice(Math.min(sel.a, sel.h), Math.max(sel.a, sel.h) + 1);
    if (chosen.length === 0) return;
    const rects: { x: number; y: number; width: number; height: number }[] = [];
    for (const w of chosen) {
      const last = rects[rects.length - 1];
      const sameLine = last && Math.abs(w.y + w.h / 2 - (last.y + last.height / 2)) <= w.h * 0.6;
      if (sameLine) {
        const x = Math.min(last!.x, w.x);
        const y = Math.min(last!.y, w.y);
        last!.width = Math.max(last!.x + last!.width, w.x + w.w) - x;
        last!.height = Math.max(last!.y + last!.height, w.y + w.h) - y;
        last!.x = x;
        last!.y = y;
      } else {
        rects.push({ x: w.x, y: w.y, width: w.w, height: w.h });
      }
    }
    const quote = chosen
      .map((w) => w.text)
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 500);
    if (!quote) return;
    const x = Math.min(...rects.map((r) => r.x));
    const y = Math.min(...rects.map((r) => r.y));
    const width = Math.max(...rects.map((r) => r.x + r.width)) - x;
    const height = Math.max(...rects.map((r) => r.y + r.height)) - y;
    clearTouchSelection();
    state.current.onPlaceAnchor('TEXT', { x, y, width, height, rects, quote });
  };

  // Long-press-and-drag to select text with a finger. A press that stays put for
  // ~450ms grabs the word underneath and hands the rest of the gesture to
  // selection (OpenSeadragon's own navigation is switched off for the duration so
  // the page doesn't pan out from under it); dragging extends word by word.
  // Listeners sit on the container: touch pointers are implicitly captured by the
  // OSD canvas, but their events still bubble up to here.
  useEffect(() => {
    const el = zoomHostRef.current;
    if (!el) return;
    let timer: number | null = null;
    let start: { x: number; y: number } | null = null;
    let active = false;
    const stopTimer = () => {
      if (timer !== null) window.clearTimeout(timer);
      timer = null;
    };
    const setNav = (on: boolean) => {
      const viewer = viewerRef.current;
      if (viewer) viewer.setMouseNavEnabled(on);
    };
    const finish = () => {
      if (!active) return;
      active = false;
      setNav(true);
      setTouchSelActive(touchSelRef.current !== null);
    };
    const down = (e: PointerEvent) => {
      if (e.pointerType !== 'touch') return;
      const s = state.current;
      if (!e.isPrimary) {
        // A second finger means pinch-zoom — abandon any in-progress selection.
        stopTimer();
        start = null;
        finish();
        return;
      }
      if (s.tool !== 'text' || s.interactive === false || wordsRef.current.length === 0) return;
      start = { x: e.clientX, y: e.clientY };
      stopTimer();
      timer = window.setTimeout(() => {
        timer = null;
        const from = start;
        if (!from) return;
        const i = wordIndexAt(from.x, from.y, 24);
        if (i < 0) return;
        active = true;
        tapGuardRef.current = true;
        setNav(false);
        touchSelRef.current = { a: i, h: i };
        paintTouchSelection();
        setTouchSelActive(true);
        navigator.vibrate?.(15);
      }, 450);
    };
    const move = (e: PointerEvent) => {
      if (e.pointerType !== 'touch') return;
      if (active) {
        e.preventDefault();
        const sel = touchSelRef.current;
        const i = wordIndexAt(e.clientX, e.clientY, Number.POSITIVE_INFINITY);
        if (sel && i >= 0 && i !== sel.h) {
          touchSelRef.current = { a: sel.a, h: i };
          paintTouchSelection();
        }
        return;
      }
      // Moving before the press lands means the user meant to pan or swipe.
      if (start && Math.hypot(e.clientX - start.x, e.clientY - start.y) > 10) {
        stopTimer();
        start = null;
      }
    };
    const up = (e: PointerEvent) => {
      if (e.pointerType !== 'touch') return;
      stopTimer();
      const from = start;
      start = null;
      if (active) {
        finish();
        return;
      }
      // Tap-to-select. Holding for 450ms to comment on a line is a lot to ask of
      // someone reviewing a proof on a phone, so a plain tap on a word selects
      // that word and raises the same "Comment on selection" button; hold-and-
      // drag is still there to take in more than one word. A tap that lands off
      // the text (an image, a margin) falls through and drops a pin as before.
      // Runs before the React pointerup handler — this listener is on the host
      // element, React's is delegated to the root — so the guard lands in time.
      const s = state.current;
      const target = e.target as HTMLElement | null;
      // Never re-select from under the floating UI: the "Comment on selection"
      // button sits inside this element, and its pointerup bubbles here first —
      // grabbing the word beneath it would swap the selection out from under the
      // click that was about to commit it. Markers keep opening their thread.
      if (target?.closest?.('.ksq-no-pin, .ksq-marker')) {
        finish();
        return;
      }
      if (
        s.tool === 'text' &&
        s.interactive !== false &&
        wordsRef.current.length > 0 &&
        from &&
        Math.hypot(e.clientX - from.x, e.clientY - from.y) <= 10
      ) {
        const i = wordIndexAt(e.clientX, e.clientY, 12);
        if (i >= 0) {
          tapGuardRef.current = true;
          touchSelRef.current = { a: i, h: i };
          paintTouchSelection();
          setTouchSelActive(true);
          navigator.vibrate?.(8);
          return;
        }
      }
      finish();
    };
    el.addEventListener('pointerdown', down);
    el.addEventListener('pointermove', move, { passive: false });
    el.addEventListener('pointerup', up);
    el.addEventListener('pointercancel', up);
    return () => {
      stopTimer();
      el.removeEventListener('pointerdown', down);
      el.removeEventListener('pointermove', move);
      el.removeEventListener('pointerup', up);
      el.removeEventListener('pointercancel', up);
    };
  }, []);

  // Switching tools (or panning with space) abandons a touch selection, and must
  // hand navigation back to OpenSeadragon in case it's switched off mid-gesture.
  useEffect(() => {
    if (tool === 'text' && !spaceHeld) return;
    touchSelRef.current = null;
    tapGuardRef.current = false;
    setTouchSelActive(false);
    viewerRef.current?.setMouseNavEnabled(true);
  }, [tool, spaceHeld]);

  // Hold space to pan: suppress the active tool and let OpenSeadragon drag-pan.
  useEffect(() => {
    const isTyping = (t: EventTarget | null) => {
      const el = t as HTMLElement | null;
      return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
    };
    const down = (e: KeyboardEvent) => {
      if (e.code === 'Space' && !e.repeat && !isTyping(e.target)) {
        e.preventDefault(); // don't scroll the page
        setSpaceHeld(true);
      }
    };
    const up = (e: KeyboardEvent) => {
      if (e.code === 'Space') setSpaceHeld(false);
    };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
    };
  }, []);

  // Redraw comment overlays, then re-add the current search highlight (if any),
  // since clearOverlays() drops everything.
  const drawAll = (viewer: OpenSeadragon.Viewer) => {
    redrawOverlays(viewer, state.current);
    const m = searchMatchRef.current;
    const p = pageRef.current;
    if (m && p && m.pageIndex === state.current.pageIndex) {
      const el = document.createElement('div');
      el.style.background = 'rgba(250,204,21,0.5)';
      el.style.outline = '2px solid #eab308';
      el.style.borderRadius = '2px';
      el.style.pointerEvents = 'none';
      viewer.addOverlay({
        element: el,
        location: viewer.viewport.imageToViewportRectangle(
          new OpenSeadragon.Rect(m.x * p.rasterWidth, m.y * p.rasterHeight, m.w * p.rasterWidth, m.h * p.rasterHeight),
        ),
      });
    }
  };
  // Center the page on the selected comment, if it lives on the open page.
  const panToSelectedThread = (viewer: OpenSeadragon.Viewer) => {
    const s = state.current;
    const p = pageRef.current;
    if (!p || !viewer.isOpen() || s.interactive === false) return;
    const t = s.threads.find((th) => th.id === s.selectedThreadId && th.pageIndex === s.pageIndex);
    if (!t) return;
    const cx = (t.anchor.x + (t.anchor.width ?? 0) / 2) * p.rasterWidth;
    const cy = (t.anchor.y + (t.anchor.height ?? 0) / 2) * p.rasterHeight;
    viewer.viewport.panTo(viewer.viewport.imageToViewportCoordinates(new OpenSeadragon.Point(cx, cy)));
    viewer.viewport.applyConstraints();
  };
  const panToMatch = (viewer: OpenSeadragon.Viewer, m: SearchMatch) => {
    const p = pageRef.current;
    if (!p) return;
    viewer.viewport.panTo(
      viewer.viewport.imageToViewportCoordinates(
        new OpenSeadragon.Point((m.x + m.w / 2) * p.rasterWidth, (m.y + m.h / 2) * p.rasterHeight),
      ),
    );
    viewer.viewport.applyConstraints();
  };

  // Open the selected page; redraw overlays once its geometry is known.
  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer || !page) return;
    const onOpen = () => {
      drawAll(viewer);
      const m = searchMatchRef.current;
      // A search hit wins; otherwise land on the comment that brought us here
      // (selecting a thread from another page changes the page and the selection
      // in one go, so the pan has to happen once the new page is actually open).
      if (m && m.pageIndex === state.current.pageIndex) panToMatch(viewer, m);
      else panToSelectedThread(viewer);
    };
    viewer.addOnceHandler('open', onOpen);
    viewer.open(tileSourceFor(page));
    return () => viewer.removeHandler('open', onOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page?.id]);

  // Redraw overlays when any overlay-affecting input changes.
  useEffect(() => {
    const viewer = viewerRef.current;
    if (viewer && page && viewer.isOpen()) drawAll(viewer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.threads, props.pending, props.selectedThreadId, tool]);

  // Selecting a comment (in the panel or by clicking its marker) recenters the
  // page on that annotation, so the two stay in sync both ways.
  useEffect(() => {
    const viewer = viewerRef.current;
    if (viewer) panToSelectedThread(viewer);
  }, [props.selectedThreadId]);

  // ── Drawing: convert element-pixel coords to normalized image coords ────────
  const toNorm = (clientX: number, clientY: number): { x: number; y: number } | null => {
    const viewer = viewerRef.current;
    const cap = captureRef.current;
    const p = pageRef.current;
    if (!viewer || !cap || !p) return null;
    const rect = cap.getBoundingClientRect();
    const pixel = new OpenSeadragon.Point(clientX - rect.left, clientY - rect.top);
    const img = viewer.viewport.viewportToImageCoordinates(viewer.viewport.pointFromPixel(pixel));
    return { x: clamp01(img.x / p.rasterWidth), y: clamp01(img.y / p.rasterHeight) };
  };
  const localPx = (e: ReactPointerEvent): [number, number] => {
    const rect = captureRef.current!.getBoundingClientRect();
    return [e.clientX - rect.left, e.clientY - rect.top];
  };

  const onPointerDown = (e: ReactPointerEvent) => {
    if (!tool) return;
    // This capture layer covers the whole page, so a tap on an existing marker
    // lands here rather than on the marker and would drop a new anchor on top of
    // it. Selecting the thread it belongs to is what that tap means.
    const marker = markerAt(e.clientX, e.clientY);
    if (marker) {
      props.onSelectThread(marker);
      return;
    }
    e.currentTarget.setPointerCapture(e.pointerId);
    const [px, py] = localPx(e);
    if (tool === 'pin') {
      const n = toNorm(e.clientX, e.clientY);
      if (n) props.onPlaceAnchor('PIN', { x: n.x, y: n.y });
    } else if (tool === 'box') {
      setDrag({ kind: 'box', x0: px, y0: py, x1: px, y1: py });
    } else {
      setDrag({ kind: 'freehand', px: [[px, py]] });
    }
  };

  const onPointerMove = (e: ReactPointerEvent) => {
    if (!drag) return;
    const [px, py] = localPx(e);
    if (drag.kind === 'box') {
      setDrag({ ...drag, x1: px, y1: py });
    } else {
      const last = drag.px[drag.px.length - 1]!;
      if (Math.hypot(px - last[0], py - last[1]) >= 3) setDrag({ kind: 'freehand', px: [...drag.px, [px, py]] });
    }
  };

  const onPointerUp = () => {
    if (!drag) return;
    if (drag.kind === 'box') {
      const start = toNormPx(drag.x0, drag.y0);
      const end = toNormPx(drag.x1, drag.y1);
      if (start && end) {
        const x = Math.min(start.x, end.x);
        const y = Math.min(start.y, end.y);
        const width = Math.abs(end.x - start.x);
        const height = Math.abs(end.y - start.y);
        if (width > 0.006 && height > 0.006) props.onPlaceAnchor('BOX', { x, y, width, height });
      }
    } else {
      const pts = drag.px
        .map(([px, py]) => toNormPx(px, py))
        .filter((p): p is { x: number; y: number } => p !== null);
      if (pts.length >= 2) {
        const xs = pts.map((p) => p.x);
        const ys = pts.map((p) => p.y);
        const x = Math.min(...xs);
        const y = Math.min(...ys);
        props.onPlaceAnchor('FREEHAND', {
          x,
          y,
          width: Math.max(Math.max(...xs) - x, 0.002),
          height: Math.max(Math.max(...ys) - y, 0.002),
          points: pts,
        });
      }
    }
    setDrag(null);
  };

  // Element-pixel → normalized image coords.
  const toNormPx = (px: number, py: number): { x: number; y: number } | null => {
    const viewer = viewerRef.current;
    const p = pageRef.current;
    if (!viewer || !p) return null;
    const img = viewer.viewport.viewportToImageCoordinates(
      viewer.viewport.pointFromPixel(new OpenSeadragon.Point(px, py)),
    );
    return { x: clamp01(img.x / p.rasterWidth), y: clamp01(img.y / p.rasterHeight) };
  };

  // Turn a text selection into a TEXT anchor. Highlight rects and the quote come
  // straight from the native selection, so selecting only part of a word anchors
  // exactly those characters rather than snapping to the whole word.
  const onTextSelect = () => {
    const sel = window.getSelection();
    const layer = textLayerRef.current;
    if (!sel || sel.isCollapsed || sel.rangeCount === 0 || !layer) return;
    const range = sel.getRangeAt(0);
    // The layer's box spans the whole page, so normalising selection rects by it
    // yields image coordinates independent of the current zoom/pan.
    const layerRect = layer.getBoundingClientRect();
    if (layerRect.width < 1 || layerRect.height < 1) return;
    const rects = Array.from(range.getClientRects())
      .filter((r) => r.width > 0.5 && r.height > 0.5)
      .map((r) => ({
        x: clamp01((r.left - layerRect.left) / layerRect.width),
        y: clamp01((r.top - layerRect.top) / layerRect.height),
        width: r.width / layerRect.width,
        height: r.height / layerRect.height,
      }));
    if (rects.length === 0) return;

    // Quote: the selected substring of each intersected word span, in reading
    // (DOM) order — clipped to the caret offsets on the first and last spans.
    const quote = (Array.from(layer.querySelectorAll('span')) as HTMLSpanElement[])
      .filter((s) => range.intersectsNode(s))
      .map((s) => {
        const text = s.textContent ?? '';
        const node = s.firstChild;
        const start = node === range.startContainer ? range.startOffset : 0;
        const end = node === range.endContainer ? range.endOffset : text.length;
        return text.slice(start, end);
      })
      .filter((t) => t.length > 0)
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 500);
    if (!quote) return;

    const x = Math.min(...rects.map((r) => r.x));
    const y = Math.min(...rects.map((r) => r.y));
    const width = Math.max(...rects.map((r) => r.x + r.width)) - x;
    const height = Math.max(...rects.map((r) => r.y + r.height)) - y;
    sel.removeAllRanges();
    props.onPlaceAnchor('TEXT', { x, y, width, height, rects, quote });
  };

  // Client pixel → normalized image coords (via the current viewport).
  const clientToNorm = (clientX: number, clientY: number): { x: number; y: number } | null => {
    const viewer = viewerRef.current;
    const el = zoomHostRef.current;
    const p = pageRef.current;
    if (!viewer || !el || !p || !viewer.isOpen()) return null;
    const rect = el.getBoundingClientRect();
    const img = viewer.viewport.viewportToImageCoordinates(
      viewer.viewport.pointFromPixel(new OpenSeadragon.Point(clientX - rect.left, clientY - rect.top)),
    );
    return { x: clamp01(img.x / p.rasterWidth), y: clamp01(img.y / p.rasterHeight) };
  };

  // In the default (text) mode a drag selects text while a plain click drops a
  // pin — so reviewers rarely need to switch tools. A drag past a small
  // threshold is treated as a pan/selection, never a pin.
  const onViewerPointerDown = (e: ReactPointerEvent) => {
    if (e.button === 0) pointerDownRef.current = { x: e.clientX, y: e.clientY };
  };
  const onViewerPointerUp = (e: ReactPointerEvent) => {
    if (e.button !== 0) return;
    const down = pointerDownRef.current;
    pointerDownRef.current = null;
    const target = e.target as HTMLElement;
    const sel = window.getSelection();
    if (sel && !sel.isCollapsed) {
      onTextSelect();
      return;
    }
    // Floating UI (search, toolbar, the selection bar) never drops a pin and must
    // not clear a touch selection out from under its own buttons.
    if (target.closest('.ksq-no-pin')) return;
    // The release that ends a long-press selection is not a tap.
    if (tapGuardRef.current) {
      tapGuardRef.current = false;
      return;
    }
    // With a touch selection showing, a tap anywhere else dismisses it.
    if (touchSelRef.current) {
      clearTouchSelection();
      return;
    }
    // A drag past the threshold is a pan/selection, not a tap. Fingers wobble far
    // more than a mouse, so touch gets a wider tolerance (still well under the
    // 60px a page swipe needs).
    const slop = e.pointerType === 'touch' ? 14 : 5;
    if (!down || Math.hypot(e.clientX - down.x, e.clientY - down.y) > slop) return;
    // Tapping an existing marker means "show me that comment", never "start a new
    // one". Hit-tested by coordinate rather than e.target: OpenSeadragon takes
    // pointer capture on its own canvas, so the pointerup that ends a tap on a
    // marker is retargeted to the canvas and a target-based check misses it.
    const marker = markerAt(e.clientX, e.clientY);
    if (marker) {
      props.onSelectThread(marker);
      return;
    }
    const n = clientToNorm(e.clientX, e.clientY);
    if (n) props.onPlaceAnchor('PIN', { x: n.x, y: n.y });
  };

  // Zoom helpers for the floating toolbar (act directly on the viewer).
  const zoomBy = (factor: number) => {
    const v = viewerRef.current;
    if (v && v.isOpen()) v.viewport.zoomBy(factor).applyConstraints();
  };
  const zoomFit = () => viewerRef.current?.viewport.goHome();

  // ── In-document search ─────────────────────────────────────────────────────
  const goToMatch = (i: number, list: SearchMatch[] = matches) => {
    const m = list[i];
    if (!m) return;
    setMatchIdx(i);
    searchMatchRef.current = m;
    const viewer = viewerRef.current;
    if (m.pageIndex !== state.current.pageIndex) {
      props.onPageChange(m.pageIndex); // page-open handler draws + pans
    } else if (viewer && viewer.isOpen()) {
      drawAll(viewer);
      panToMatch(viewer, m);
    }
  };
  const runSearch = async (q: string) => {
    setSearch(q);
    const query = q.trim().toLowerCase();
    if (!query) {
      setMatches([]);
      setMatchIdx(-1);
      searchMatchRef.current = null;
      const v = viewerRef.current;
      if (v && v.isOpen()) drawAll(v);
      return;
    }
    const found: SearchMatch[] = [];
    for (let i = 0; i < pages.length; i++) {
      let words = textCacheRef.current.get(i);
      if (!words) {
        const pg = pages[i];
        try {
          words = pg?.textUrl
            ? ((await fetch(pg.textUrl, { credentials: 'include' }).then((r) => r.json())).words ?? [])
            : [];
        } catch {
          words = [];
        }
        textCacheRef.current.set(i, words!);
      }
      for (const w of words!) {
        if ((w.text ?? '').toLowerCase().includes(query)) {
          found.push({ pageIndex: i, x: w.x, y: w.y, w: w.w, h: w.h });
        }
      }
    }
    setMatches(found);
    setMatchIdx(-1);
    searchMatchRef.current = null;
    const v = viewerRef.current;
    if (v && v.isOpen()) drawAll(v);
  };
  const step = (dir: 1 | -1) => {
    if (!matches.length) return;
    const i = matchIdx < 0 ? (dir === 1 ? 0 : matches.length - 1) : (matchIdx + dir + matches.length) % matches.length;
    goToMatch(i);
  };

  // Pan mode: the hand tool, or the space bar held over any other tool. Both let
  // OpenSeadragon drag-pan the page and suppress the annotation-capture layer.
  const panning = spaceHeld || tool === 'hand';

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex min-h-0 flex-1">
        <div
          ref={zoomHostRef}
          className="relative min-h-0 flex-1 overflow-hidden"
          style={panning ? { cursor: 'grab' } : undefined}
          onPointerDown={tool === 'text' && !spaceHeld ? onViewerPointerDown : undefined}
          onPointerUp={tool === 'text' && !spaceHeld ? onViewerPointerUp : undefined}
        >
          {/* Leave room for the mobile bottom bar so it never covers the page.
              (Its top-left corner still matches the overlay layers, which is what
              the pixel→image coordinate maths relies on.) */}
          <div
            ref={hostRef}
            className={`absolute inset-x-0 top-0 bg-neutral-100 ${
              props.interactive !== false ? 'bottom-[45px] md:bottom-0' : 'bottom-0'
            }`}
          />

          {/* Wheel controls hint (desktop only — the bottom bar carries page nav
              on phones, where there is no wheel). */}
          {props.interactive !== false && pages.length > 1 && (
            <span className="ksq-no-pin pointer-events-none absolute bottom-2 left-2 z-20 hidden rounded bg-black/55 px-2 py-0.5 text-[11px] text-white md:block">
              Scroll to change pages · Ctrl+scroll to zoom
            </span>
          )}

          {/* In-document search */}
          {props.interactive !== false && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              step(1);
            }}
            className="ksq-no-pin absolute left-2 top-2 z-20 flex items-center gap-1.5 rounded-lg border border-neutral-200 bg-white/95 px-2.5 py-1.5 shadow-sm backdrop-blur"
          >
            <IconSearch className="h-4 w-4 text-neutral-400" />
            <input
              value={search}
              onChange={(e) => void runSearch(e.target.value)}
              placeholder="Search…"
              className="w-28 bg-transparent text-xs text-neutral-700 outline-none placeholder:text-neutral-400"
            />
            {search && (
              <span className="whitespace-nowrap text-[10px] text-neutral-400">
                {matches.length ? `${matchIdx + 1}/${matches.length}` : '0'}
              </span>
            )}
            <button
              type="button"
              onClick={() => step(-1)}
              disabled={!matches.length}
              title="Previous match"
              className="text-neutral-500 hover:text-neutral-900 disabled:opacity-30"
            >
              <IconChevronUp className="h-4 w-4" />
            </button>
            <button
              type="submit"
              disabled={!matches.length}
              title="Next match"
              className="text-neutral-500 hover:text-neutral-900 disabled:opacity-30"
            >
              <IconChevronDown className="h-4 w-4" />
            </button>
            {search && (
              <button
                type="button"
                onClick={() => void runSearch('')}
                title="Clear search"
                className="ml-0.5 border-l border-neutral-200 pl-1.5 text-xs text-neutral-400 hover:text-neutral-700"
              >
                ✕
              </button>
            )}
          </form>
          )}

          {/* Tool + zoom toolbar. Desktop: a floating vertical pill on the right.
              Phones: a full-width horizontal bar pinned to the bottom, with page
              nav on the left (there's no wheel and no thumbnail rail there).
              `md:contents` dissolves the mobile grouping so the buttons flow
              straight into the vertical pill on desktop. */}
          {props.interactive !== false && (
            <div
              data-tour="pdf-tools"
              className="ksq-no-pin absolute inset-x-0 bottom-0 z-20 flex items-center gap-1 border-t border-neutral-200 bg-white/95 px-2 py-1.5 backdrop-blur md:inset-x-auto md:bottom-auto md:right-3 md:top-1/2 md:w-auto md:-translate-y-1/2 md:flex-col md:items-center md:rounded-xl md:border md:p-1.5 md:shadow-md"
            >
              {pages.length > 1 && (
                <div className="flex items-center gap-0.5 md:hidden">
                  <button
                    type="button"
                    onClick={() => props.onPageChange(Math.max(0, pageIndex - 1))}
                    disabled={pageIndex === 0}
                    title="Previous page"
                    aria-label="Previous page"
                    className="flex h-8 w-8 items-center justify-center rounded-lg text-neutral-500 disabled:opacity-30"
                  >
                    <IconChevronDown className="h-[18px] w-[18px] rotate-90" />
                  </button>
                  <span className="min-w-[3.25rem] text-center text-xs font-medium tabular-nums text-neutral-600">
                    {pageIndex + 1} / {pages.length}
                  </span>
                  <button
                    type="button"
                    onClick={() => props.onPageChange(Math.min(pages.length - 1, pageIndex + 1))}
                    disabled={pageIndex >= pages.length - 1}
                    title="Next page"
                    aria-label="Next page"
                    className="flex h-8 w-8 items-center justify-center rounded-lg text-neutral-500 disabled:opacity-30"
                  >
                    <IconChevronDown className="h-[18px] w-[18px] -rotate-90" />
                  </button>
                </div>
              )}
              <div className={`flex items-center gap-1 md:contents ${pages.length > 1 ? 'ml-auto' : 'mx-auto'}`}>
                {TOOLBAR.map((t) => (
                  <button
                    key={t.tool}
                    type="button"
                    onClick={() => props.onSelectTool?.(t.tool)}
                    title={t.label}
                    aria-pressed={tool === t.tool}
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors ${
                      tool === t.tool
                        ? 'bg-blue-600 text-white'
                        : 'text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900'
                    }`}
                  >
                    <t.Icon className="h-[18px] w-[18px]" />
                  </button>
                ))}
                {/* Zoom is desktop-only — touch pinch-zooms the page directly. */}
                <span className="my-0.5 hidden h-px w-6 bg-neutral-200 md:block" />
                <button
                  type="button"
                  onClick={() => zoomBy(1.4)}
                  title="Zoom in"
                  className="hidden h-8 w-8 items-center justify-center rounded-lg text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900 md:flex"
                >
                  <IconZoomIn className="h-[18px] w-[18px]" />
                </button>
                <button
                  type="button"
                  onClick={() => zoomBy(0.7)}
                  title="Zoom out"
                  className="hidden h-8 w-8 items-center justify-center rounded-lg text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900 md:flex"
                >
                  <IconZoomOut className="h-[18px] w-[18px]" />
                </button>
                <button
                  type="button"
                  onClick={zoomFit}
                  title="Fit to view"
                  className="hidden h-8 w-8 items-center justify-center rounded-lg text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900 md:flex"
                >
                  <IconFit className="h-[18px] w-[18px]" />
                </button>
              </div>
            </div>
          )}
          {/* Touch text selection: what to do with it, and how to start one. Both
              sit just above the mobile bottom bar. */}
          {props.interactive !== false && coarse && tool === 'text' && (
            touchSelActive ? (
              <div className="ksq-no-pin absolute inset-x-0 bottom-[52px] z-20 flex justify-center gap-2 px-3">
                <button
                  type="button"
                  onClick={commitTouchSelection}
                  className="rounded-full bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-lg"
                >
                  Comment on selection
                </button>
                <button
                  type="button"
                  onClick={clearTouchSelection}
                  className="rounded-full border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-600 shadow-lg"
                >
                  Cancel
                </button>
              </div>
            ) : (
              !props.pending && (
                <span className="ksq-no-pin pointer-events-none absolute inset-x-3 bottom-[52px] z-20 mx-auto w-fit max-w-full rounded-full bg-black/55 px-3 py-1 text-center text-[11px] text-white">
                  Tap text to select · hold to drag over more · tap elsewhere to pin
                </span>
              )
            )
          )}

          {/* Selectable text layer — present in text mode (but yielded while space
              is held for panning). The container ignores pointer events so drags
              over empty space still pan; only the word spans capture selection. */}
          <div
            ref={textLayerRef}
            className="ksq-text-layer absolute left-0 top-0 z-10"
            style={{ display: tool === 'text' && !spaceHeld ? 'block' : 'none', pointerEvents: 'none' }}
          />
          {tool && tool !== 'text' && tool !== 'hand' && !spaceHeld && (
            <div
              ref={captureRef}
              className="absolute inset-0 z-10 touch-none"
              style={{ cursor: 'crosshair' }}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
            >
              <DragPreview drag={drag} tool={tool} />
            </div>
          )}
        </div>

        {pages.length > 1 && props.interactive !== false && (
          <div className="hidden w-28 shrink-0 space-y-2 overflow-y-auto border-l border-neutral-200 bg-white p-2 sm:block">
            {pages.map((p, i) => {
              const count = props.threads.filter((t) => t.pageIndex === i).length;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => props.onPageChange(i)}
                  className={`relative block w-full overflow-hidden rounded border ${
                    i === pageIndex
                      ? 'border-neutral-900 ring-1 ring-neutral-900'
                      : 'border-neutral-200 hover:border-neutral-400'
                  }`}
                  title={`Page ${i + 1}`}
                >
                  <img src={p.thumbnailUrl} alt={`Page ${i + 1}`} className="block w-full bg-white" loading="lazy" />
                  {count > 0 && (
                    <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-blue-600 px-1 text-[10px] font-semibold text-white">
                      {count}
                    </span>
                  )}
                  <span className="block bg-white py-0.5 text-center text-[10px] text-neutral-500">
                    {i + 1}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/** Live preview of the in-progress drag, drawn in element-pixel space. */
function DragPreview({ drag, tool }: { drag: Drag | null; tool: DrawTool }) {
  if (!drag) return null;
  const stroke = tool === 'box' ? '#2563eb' : '#7c3aed';
  return (
    <svg className="pointer-events-none absolute inset-0 h-full w-full">
      {drag.kind === 'box' ? (
        <rect
          x={Math.min(drag.x0, drag.x1)}
          y={Math.min(drag.y0, drag.y1)}
          width={Math.abs(drag.x1 - drag.x0)}
          height={Math.abs(drag.y1 - drag.y0)}
          fill="rgba(37,99,235,0.08)"
          stroke={stroke}
          strokeWidth={2}
          strokeDasharray="5 4"
        />
      ) : (
        <polyline
          points={drag.px.map(([x, y]) => `${x},${y}`).join(' ')}
          fill="none"
          stroke={stroke}
          strokeWidth={2.5}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      )}
    </svg>
  );
}

/**
 * Order word boxes into human reading order for native text selection. Browser
 * selection follows DOM order, so the layout must match how the eye reads:
 *
 *  1. Split the page into vertical blocks at line-gap *outliers* — a full-width
 *     heading sits above the body with a bigger gap than the body's own line
 *     pitch, so it lands in its own block instead of being sliced by the column
 *     split (which would teleport the heading's right half to the next column
 *     and make selection jump).
 *  2. Within each block, lay out column-by-column (all of the left column
 *     top→bottom, then the next) so dragging down one column never grabs its
 *     neighbour. A block with no gutter (a heading, a single column) just reads
 *     top→bottom, left→right.
 */
function readingOrder(words: TextWord[]): TextWord[] {
  if (words.length < 2) return words;
  return segmentBlocks(words).flatMap(orderBlock);
}

/** Lay out one block column-by-column; blocks with no gutter read straight. */
function orderBlock(words: TextWord[]): TextWord[] {
  const boundaries = detectColumnBoundaries(words);
  if (boundaries.length === 0) return orderColumn(words);
  const byColumn = new Map<number, TextWord[]>();
  for (const wd of words) {
    let c = 0;
    for (const b of boundaries) if (wd.x >= b) c++; // assign by left edge
    const bucket = byColumn.get(c);
    if (bucket) bucket.push(wd);
    else byColumn.set(c, [wd]);
  }
  const out: TextWord[] = [];
  for (const c of [...byColumn.keys()].sort((a, b) => a - b)) {
    out.push(...orderColumn(byColumn.get(c)!));
  }
  return out;
}

/**
 * Split words into vertical blocks separated by full-width whitespace. Words are
 * clustered into lines (columns share baselines, so a line spans the full width);
 * a gap between consecutive lines that is much larger than the median line pitch
 * marks a block boundary (heading above body, section break, etc.).
 */
function segmentBlocks(words: TextWord[]): TextWord[][] {
  const lines = clusterLines(words);
  if (lines.length < 4) return [words]; // too few lines to judge; keep as one block
  const gaps: number[] = [];
  for (let i = 1; i < lines.length; i++) gaps.push(lines[i]!.cy - lines[i - 1]!.cy);
  const threshold = median(gaps) * 1.5;
  const blocks: TextWord[][] = [[]];
  for (let i = 0; i < lines.length; i++) {
    if (i > 0 && lines[i]!.cy - lines[i - 1]!.cy > threshold) blocks.push([]);
    blocks[blocks.length - 1]!.push(...lines[i]!.words);
  }
  return blocks;
}

/** Cluster words into lines by vertical centre (columns share baselines). */
function clusterLines(words: TextWord[]): { cy: number; words: TextWord[] }[] {
  const sorted = [...words].sort((a, b) => a.y + a.h / 2 - (b.y + b.h / 2));
  const lines: { cy: number; words: TextWord[] }[] = [];
  for (const wd of sorted) {
    const cy = wd.y + wd.h / 2;
    const line = lines[lines.length - 1];
    if (line && Math.abs(cy - line.cy) <= wd.h * 0.6) {
      line.words.push(wd);
      line.cy = (line.cy * (line.words.length - 1) + cy) / line.words.length;
    } else {
      lines.push({ cy, words: [wd] });
    }
  }
  return lines;
}

function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  return sorted[Math.floor(sorted.length / 2)] ?? 0;
}

/** Top→bottom, left→right within a single column (words clustered into lines). */
function orderColumn(words: TextWord[]): TextWord[] {
  const sorted = [...words].sort((a, b) => a.y - b.y);
  const lines: TextWord[][] = [];
  for (const wd of sorted) {
    const line = lines[lines.length - 1];
    if (line && Math.abs(wd.y - line[0]!.y) <= wd.h * 0.6) line.push(wd);
    else lines.push([wd]);
  }
  return lines.flatMap((l) => l.sort((a, b) => a.x - b.x));
}

/**
 * Detect x-positions of column gutters from where words *start*. A histogram of
 * word left-edges shows a clear empty band in the gutter: every line in a column
 * starts at/after that column's left margin, so no word begins inside the gutter.
 * (A coverage histogram fails here — justified lines push their last word's tail
 * into the gutter, filling it.) Runs of near-empty start-bins wide enough to be a
 * real gutter split the page into columns; returns [] for single-column pages.
 */
function detectColumnBoundaries(words: TextWord[]): number[] {
  if (words.length < 30) return []; // too little text to judge a column layout
  const BINS = 100;
  const starts = new Array<number>(BINS).fill(0);
  for (const wd of words) {
    const i = Math.max(0, Math.min(BINS - 1, Math.floor(wd.x * BINS)));
    starts[i] = starts[i]! + 1;
  }
  const emptyThresh = Math.max(1, words.length * 0.004); // tolerate a few heading words
  const minGutter = Math.max(2, Math.round(BINS * 0.02));
  const boundaries: number[] = [];
  let run = 0;
  for (let i = 0; i < BINS; i++) {
    if (starts[i]! <= emptyThresh) {
      run++;
    } else {
      if (run >= minGutter) {
        const center = (i - run / 2) / BINS;
        if (center > 0.1 && center < 0.9) boundaries.push(center); // ignore page margins
      }
      run = 0;
    }
  }
  return boundaries;
}

function tileSourceFor(page: Page): OpenSeadragon.TileSourceOptions {
  const maxLevel = Math.ceil(Math.log2(Math.max(page.rasterWidth, page.rasterHeight)));
  return {
    width: page.rasterWidth,
    height: page.rasterHeight,
    tileSize: page.tileSize,
    tileOverlap: page.overlap,
    minLevel: 0,
    maxLevel,
    getTileUrl: (level: number, x: number, y: number) => `${page.tilesBaseUrl}/${level}/${x}_${y}.jpeg`,
  };
}

/** Rebuild all overlays: comment annotations + any pending preview. */
function redrawOverlays(viewer: OpenSeadragon.Viewer, s: ProofViewerProps): void {
  viewer.clearOverlays();
  const page = s.version.pages[s.pageIndex];
  if (!page) return;

  s.threads
    .filter((t) => t.pageIndex === s.pageIndex)
    .forEach((t, i) => addAnnotation(viewer, page, t.anchorType, t.anchor, {
      label: String(i + 1),
      resolved: t.status === 'RESOLVED',
      active: t.id === s.selectedThreadId,
      title: threadTooltip(t),
      onClick: () => s.onSelectThread(t.id),
      // Not while a drawing tool is armed — a hit area over an existing mark
      // would swallow the drag that draws a new one on top of it. Text mode is
      // the exception that still needs care: highlight swatches stay inert there
      // so you can select the words underneath one, while box outlines and
      // freehand strokes are thin enough not to get in the way.
      hitShape:
        s.interactive !== false &&
        (s.tool == null || s.tool === 'hand' || (s.tool === 'text' && t.anchorType !== 'TEXT')),
      threadId: t.id,
    }));

  if (s.pending) {
    addAnnotation(viewer, page, s.pending.anchorType, s.pending.anchor, {
      label: '+',
      resolved: false,
      active: true,
      pending: true,
    });
  }
}

interface AnnoOpts {
  label: string;
  resolved: boolean;
  active: boolean;
  pending?: boolean;
  /** Make the drawn shape itself clickable, not just the numbered pin. */
  hitShape?: boolean;
  /** Stamped on the marker so a hit test can map it back to its thread. */
  threadId?: string;
  /** Native hover tooltip on the marker (used to read comments in compare). */
  title?: string;
  onClick?: () => void;
}

/** First message (author: body, +N more) — shown as the marker's hover title. */
function threadTooltip(t: CommentThread): string {
  const first = t.comments[0];
  if (!first) return '';
  const who = first.author?.name ?? 'Unknown';
  const body = first.body.replace(/\s+/g, ' ').trim();
  const more = t.comments.length > 1 ? ` (+${t.comments.length - 1} more)` : '';
  return `${who}: ${body}${more}`;
}

/** Draw one annotation (pin / box / freehand) + its numbered marker. */
function addAnnotation(
  viewer: OpenSeadragon.Viewer,
  page: Page,
  type: AnchorType,
  anchor: Anchor,
  opts: AnnoOpts,
): void {
  const rw = page.rasterWidth;
  const rh = page.rasterHeight;
  const color = opts.resolved ? '#10b981' : '#2563eb';
  const dashed = opts.pending ? 'dashed' : 'solid';

  // Clicking the mark itself selects its comment. Boxes and freehand strokes get
  // an outline-only hit area (`stroke`) rather than a filled one, so dragging
  // inside a big box still pans the page instead of being eaten by the overlay.
  const hit = Boolean(opts.onClick && opts.hitShape && !opts.pending);
  const makeClickable = (el: HTMLElement | SVGElement, mode: 'all' | 'stroke') => {
    el.style.pointerEvents = mode;
    el.style.cursor = 'pointer';
    el.addEventListener('click', (e) => {
      e.stopPropagation();
      opts.onClick!();
    });
  };

  if (type === 'BOX' && anchor.width != null && anchor.height != null) {
    const el = document.createElement('div');
    el.style.border = `2px ${dashed} ${color}`;
    el.style.background = 'rgba(37,99,235,0.06)';
    el.style.boxSizing = 'border-box';
    el.style.pointerEvents = 'none';
    const location = viewer.viewport.imageToViewportRectangle(
      new OpenSeadragon.Rect(anchor.x * rw, anchor.y * rh, anchor.width * rw, anchor.height * rh),
    );
    viewer.addOverlay({ element: el, location });
    if (hit) {
      // A transparent, fat-stroked rect sitting on top of the border, added
      // second so it wins the hit test against the fill underneath.
      const svg = document.createElementNS(NS, 'svg');
      svg.setAttribute('viewBox', '0 0 100 100');
      svg.setAttribute('preserveAspectRatio', 'none');
      svg.style.width = '100%';
      svg.style.height = '100%';
      svg.style.overflow = 'visible';
      svg.style.pointerEvents = 'none';
      const outline = document.createElementNS(NS, 'rect');
      outline.setAttribute('x', '0');
      outline.setAttribute('y', '0');
      outline.setAttribute('width', '100');
      outline.setAttribute('height', '100');
      outline.setAttribute('fill', 'none');
      outline.setAttribute('stroke', 'transparent');
      outline.setAttribute('stroke-width', '12');
      outline.setAttribute('vector-effect', 'non-scaling-stroke');
      makeClickable(outline, 'stroke');
      svg.appendChild(outline);
      viewer.addOverlay({ element: svg as unknown as HTMLElement, location });
    }
  } else if (type === 'TEXT' && anchor.rects) {
    for (const r of anchor.rects) {
      const el = document.createElement('div');
      el.style.background = opts.resolved ? 'rgba(16,185,129,0.28)' : 'rgba(245,158,11,0.35)';
      el.style.pointerEvents = 'none';
      if (opts.pending) el.style.outline = '1px dashed #f59e0b';
      // Highlights are small and sit on text, so the whole swatch is the target.
      if (hit) makeClickable(el, 'all');
      viewer.addOverlay({
        element: el,
        location: viewer.viewport.imageToViewportRectangle(
          new OpenSeadragon.Rect(r.x * rw, r.y * rh, r.width * rw, r.height * rh),
        ),
      });
    }
  } else if (type === 'FREEHAND' && anchor.points && anchor.width != null && anchor.height != null) {
    const svg = document.createElementNS(NS, 'svg');
    svg.setAttribute('viewBox', `0 0 ${anchor.width * rw} ${anchor.height * rh}`);
    svg.setAttribute('preserveAspectRatio', 'none');
    svg.style.width = '100%';
    svg.style.height = '100%';
    svg.style.overflow = 'visible';
    svg.style.pointerEvents = 'none';
    const poly = document.createElementNS(NS, 'polyline');
    poly.setAttribute(
      'points',
      anchor.points.map((p) => `${(p.x - anchor.x) * rw},${(p.y - anchor.y) * rh}`).join(' '),
    );
    poly.setAttribute('fill', 'none');
    poly.setAttribute('stroke', color);
    poly.setAttribute('stroke-width', '2.5');
    poly.setAttribute('stroke-linejoin', 'round');
    poly.setAttribute('stroke-linecap', 'round');
    poly.setAttribute('vector-effect', 'non-scaling-stroke');
    if (opts.pending) poly.setAttribute('stroke-dasharray', '5 4');
    svg.appendChild(poly);
    if (hit) {
      // Same path, drawn invisible and much thicker — a 2.5px line is too thin
      // to hit reliably, especially on touch.
      const hitLine = poly.cloneNode() as SVGPolylineElement;
      hitLine.setAttribute('stroke', 'transparent');
      hitLine.setAttribute('stroke-width', '16');
      hitLine.removeAttribute('stroke-dasharray');
      makeClickable(hitLine, 'stroke');
      svg.appendChild(hitLine);
    }
    viewer.addOverlay({
      // OSD accepts any DOM node at runtime; its types only name HTMLElement.
      element: svg as unknown as HTMLElement,
      location: viewer.viewport.imageToViewportRectangle(
        new OpenSeadragon.Rect(anchor.x * rw, anchor.y * rh, anchor.width * rw, anchor.height * rh),
      ),
    });
  }

  // Numbered marker at the anchor origin (pin position / bbox top-left).
  const marker = pinElement(opts.label, opts.resolved, opts.active);
  if (opts.threadId) marker.dataset.threadId = opts.threadId;
  if (opts.pending) marker.style.borderStyle = 'dashed';
  if (opts.title) marker.title = opts.title;
  if (opts.onClick) {
    marker.onclick = (e) => {
      e.stopPropagation();
      opts.onClick!();
    };
  }
  viewer.addOverlay({
    element: marker,
    location: viewer.viewport.imageToViewportCoordinates(new OpenSeadragon.Point(anchor.x * rw, anchor.y * rh)),
    placement: type === 'PIN' ? OpenSeadragon.Placement.CENTER : OpenSeadragon.Placement.TOP_LEFT,
  });
}

/** A circular comment marker element. */
function pinElement(label: string, resolved: boolean, active: boolean): HTMLDivElement {
  const el = document.createElement('div');
  el.className = 'ksq-marker';
  el.textContent = label;
  // OpenSeadragon forces overlay elements to `display:block`, so flex centering
  // is ignored — center the label with text-align + a line-height equal to the
  // content box height instead.
  el.style.boxSizing = 'border-box';
  el.style.width = '24px';
  el.style.height = '24px';
  el.style.borderRadius = '9999px';
  el.style.textAlign = 'center';
  el.style.fontSize = '12px';
  el.style.fontWeight = '600';
  el.style.lineHeight = '20px';
  el.style.fontFamily = 'system-ui, sans-serif';
  el.style.cursor = 'pointer';
  el.style.color = '#fff';
  el.style.userSelect = 'none';
  el.style.background = resolved ? '#10b981' : '#2563eb';
  el.style.border = `2px solid ${active ? '#f59e0b' : '#ffffff'}`;
  el.style.boxShadow = '0 1px 3px rgba(0,0,0,0.4)';
  el.style.pointerEvents = 'auto';
  return el;
}
